/* eslint-disable no-nested-ternary */
import React, { useContext, useMemo } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { space, typography, color } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import {
  VictoryTooltip,
  VictoryVoronoiContainer,
  VictoryAxis,
  VictoryGroup,
  VictoryArea,
  VictoryChart,
  VictoryLine,
} from 'victory';
import { useDimensions } from 'hooks';
import { useSelector } from 'react-redux';
import moment from 'moment';
import History from 'theme/icons/history';
import translations from 'translations';
import Label from './label';
import AreaTabs from './tabs';

const Wrapper = styled.div`
  width: 100%;
  z-index: 999;

  .VictoryContainer {
    & > svg {
      overflow: visible;
    }
  }

  @media only screen and (max-width: 768px) {
    .VictoryContainer {
      user-select: auto !important;
      touch-action: auto !important;
    }
  }
`;

const Head = styled.div`
  ${space}

  display: flex;
  /* The chart's own switch on one side and the totals on the other: on a phone
     that was one row wider than the screen, with the figures cut off at the
     right edge. Stacked below the first breakpoint, and each half wraps. */
  flex-direction: column;
  gap: ${themeGet('space.xs')}px;

  .left {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    flex: 1;
  }

  .right {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    flex: 1;
  }

  @media (min-width: ${themeGet('breakpoints.0')}) {
    flex-direction: row;
    gap: 0;

    .left,
    .right {
      flex-wrap: nowrap;
    }

    .right {
      justify-content: flex-end;
    }
  }
`;

const WrapValues = styled.div`
  display: flex;
  flex-direction: column;

  .row {
    display: flex;
    flex-direction: row;
  }
`;

const Value = styled.div`
  ${color}
  ${space}

  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const Text = styled.p`
  ${typography}
  ${color}
  ${space}

  white-space: nowrap;
  text-transform: capitalize;
`;

const Legend = styled.div`
  display: flex;
  justify-content: center;
`;

const LegendLabel = styled.span`
  ${space}

  display: flex;
  align-items: center;

  & > :before {
    ${color}

    display: inline-block;
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 6px;
  }
`;

const WrapIcon = styled.span`
  ${color}
  ${space}
`;

// Helper function to clean data and ensure all values are finite numbers
const cleanData = (data) => {
  if (!Array.isArray(data)) return [];
  return data
    .filter(datum => {
      // Filter out any data points with invalid timestamps
      if (!datum.timestamp) return false;
      const timestamp = moment(datum.timestamp);
      return timestamp.isValid();
    })
    .map(datum => {
      // Ensure y is a valid finite number
      const cleanY = typeof datum.y === 'number' && Number.isFinite(datum.y) ? datum.y : 0;
      return { ...datum, y: cleanY };
    });
};

function Area({
  title,
  temporaryRevenue,
  repartitionCharge,
  chart,
  isWithinRange,
  chartHeight,
  titleMargin,
  handleChangeTab,
  activeKey,
  dataType,
}) {
  const { languageCode } = useSelector((state) => state.config);
  const { width } = useDimensions();
  const theme = useContext(ThemeContext);

  const xAxisLength = useMemo(() => {
    if (chart?.length === 0) return 0;
    if (chart[0]?.data?.length <= 6) return 1;

    return chart[0]?.data ? Math.round(chart[0].data.length / 6) : 1;
  }, [chart]);

  const maxAxisY = useMemo(() => {
    if (chart.length === 0) return 0;

    const maxAxis = chart.reduce((max, item) => {
      const axisValue = typeof item.maxAxisY === 'number' && Number.isFinite(item.maxAxisY) ? item.maxAxisY : 0;
      return Math.max(max, axisValue);
    }, 0);

    // Ensure the result is a valid finite number
    return typeof maxAxis === 'number' && Number.isFinite(maxAxis) && maxAxis > 0 ? maxAxis : 100;
  }, [chart]);

  const yAxisPadding = useMemo(() => {
    if (maxAxisY === 0) return 0;

    return maxAxisY.toString() ? -20 - maxAxisY.toString().length * 5 : -20;
  }, [maxAxisY]);

  const PageWidth = useMemo(() => {
    // Ensure width is a valid finite number
    if (typeof width === 'number' && Number.isFinite(width) && width > 0) {
      return width;
    }
    // Fallback to a reasonable default width if width is undefined, Infinity, or invalid
    return 800;
  }, [width]);

  const { sideDate = null, value: isSideDateActive = false } = isWithinRange || {};

  // console.log('isWithinRange', sideDate, sideDateParsed, isSideDateActive);

  return (
    <Wrapper>
      <Head py="s" px="xs">
        <AreaTabs handleChangeTab={handleChangeTab} activeKey={activeKey} dataType={dataType} />
        <div className="left">
          <Text fontSize="h2" lineHeight="h2" fontWeight="bold" color="grey_400" ml={titleMargin}>
            {title}
          </Text>
        </div>

        {temporaryRevenue && (
          <div className="right">
            <WrapValues>
              <div className="row">
                <Legend>
                  {chart.filter(el => !el.id.includes('repartition')).map((el) => (
                    <LegendLabel key={el.color} px="xs" bg={el.color} mb="0">
                      <Text fontSize="pSmall" lineHeight="pSmall" fontWeight="bold" color="grey_600">
                        {el.label}
                      </Text>
                    </LegendLabel>
                  ))}
                </Legend>
              </div>
              <div className="row">
                <Text 
                  fontSize="h2" 
                  lineHeight="h2" 
                  fontWeight="bold" 
                  color={chart.find(el => !el.id.includes('repartition') && !el.id.includes('prev_'))?.color || 'area_0'}
                >
                  {temporaryRevenue.value}
                </Text>
                <Value pl="s">
                  <Text
                    fontSize="label"
                    lineHeight="label"
                    fontWeight="bold"
                    color={
                      temporaryRevenue.percent > 0
                        ? 'lime_400'
                        : temporaryRevenue.percent < 0
                        ? 'red_400'
                        : 'grey_600'
                    }
                  >
                    {`${
                      temporaryRevenue.percent > 0 ? '+' : temporaryRevenue.percent < 0 ? '-' : ''
                    } ${Math.abs(temporaryRevenue.percent)}%`}
                  </Text>
                </Value>
              </div>
              <div className="row">
                <Text 
                  fontSize="pLarge" 
                  lineHeight="pLarge" 
                  fontWeight="bold" 
                  color={chart.find(el => !el.id.includes('repartition') && el.id.includes('prev_'))?.color || 'lime_400'}
                >
                  {temporaryRevenue.previousValue}
                </Text>
                <WrapIcon color="primary_300" pl="s">
                  <History />
                </WrapIcon>
              </div>
            </WrapValues>
          </div>
        )}

        {repartitionCharge && repartitionCharge.rawValue < 0 && (
          <div className="right" style={{ marginLeft: '35px' }}>
            <WrapValues>
              <div className="row">
                <Legend>
                  {chart.filter(el => el.id.includes('repartition')).map((el) => (
                    <LegendLabel key={el.color} px="xs" bg={el.color} mb="0">
                      <Text fontSize="pSmall" lineHeight="pSmall" fontWeight="bold" color="grey_600">
                        {el.label}
                      </Text>
                    </LegendLabel>
                  ))}
                </Legend>
              </div>
              <div className="row">
                <Text 
                  fontSize="h2" 
                  lineHeight="h2" 
                  fontWeight="bold" 
                  color={chart.find(el => el.id.includes('repartition') && !el.id.includes('prev_'))?.color || 'orange_500'}
                >
                  {repartitionCharge.value}
                </Text>
                <Value pl="s">
                  <Text
                    fontSize="label"
                    lineHeight="label"
                    fontWeight="bold"
                    color={
                      repartitionCharge.percent > 0
                        ? 'lime_400'
                        : repartitionCharge.percent < 0
                        ? 'red_400'
                        : 'grey_600'
                    }
                  >
                    {`${
                      repartitionCharge.percent > 0 ? '+' : repartitionCharge.percent < 0 ? '-' : ''
                    } ${Math.abs(repartitionCharge.percent)}%`}
                  </Text>
                </Value>
              </div>
              <div className="row">
                <Text 
                  fontSize="pLarge" 
                  lineHeight="pLarge" 
                  fontWeight="bold" 
                  color={chart.find(el => el.id.includes('repartition') && el.id.includes('prev_'))?.color || 'orange_300'}
                >
                  {repartitionCharge.previousValue}
                </Text>
                <WrapIcon color="primary_300" pl="s">
                  <History />
                </WrapIcon>
              </div>
            </WrapValues>
          </div>
        )}
      </Head>

      <VictoryChart
        height={
          typeof chartHeight === 'number' && Number.isFinite(chartHeight) && chartHeight > 0
            ? chartHeight
            : 300
        }
        width={PageWidth}
        animate={{ duration: 1000 }}
        domainPadding={{ x: 0, y: 30 }}
        containerComponent={
          <VictoryVoronoiContainer
            voronoiBlacklist={['parent']}
            voronoiDimension="x"
            labels={() => ' '}
            labelComponent={
              <VictoryTooltip
                cornerRadius={4}
                flyoutStyle={{ fill: 'white', stroke: 'transparent' }}
                style={{
                  fontSize: 13,
                  fontFamily: 'inherit',
                  lineHeight: '20px',
                  fill: themeGet(`colors.grey_600`)({ theme }),
                }}
                flyoutComponent={<Label maxWidth={PageWidth} data={chart} />}
              />
            }
          />
        }
        padding={{ left: 0, right: 0, top: 0, bottom: 50 }}
      >
        <VictoryAxis
          tickFormat={(t, index) => {
            if (index % xAxisLength === 0) {
              // Converti il timestamp in una data formattata
              return moment(t).format('MMM DD');
            }
            return '';
          }}
          style={{
            axis: { stroke: 'transparent' },
            tickLabels: {
              fontFamily: 'inherit',
              fontSize: 13,
              padding: 12,
              angle: 35,
              verticalAnchor: 'end',
              textAnchor: 'start',
            },
          }}
        />
        <VictoryAxis
          tickFormat={(t) => t.toFixed(0)}
          dependentAxis
          style={{
            grid: { stroke: 'grey', strokeDasharray: '4, 8', strokeWidth: 0.5 },
            axis: { stroke: 'transparent' },
            tickLabels: {
              fontFamily: 'inherit',
              textAnchor: 'end',
              fontSize: 13,
              padding: yAxisPadding,
            },
          }}
        />
        {chart.map((el) => {
          // Divide i dati in base alla sideDate
          const sideDateTime = sideDate
            ? moment(sideDate).endOf('day').utc().startOf('day').format()
            : moment('2100-01-01').format();
          const dataBeforeSideDate = cleanData(
            el.data.filter((datum) =>
              moment(datum.timestamp).isSameOrBefore(sideDateTime),
            ),
          );
          const dataAfterSideDate = cleanData(
            el.data.filter((datum) =>
              moment(datum.timestamp).isSameOrAfter(sideDateTime),
            ),
          );

          if (el.id.match(/prev_/)) {
            return (
              <VictoryGroup key={`${el.id}`}>
                <VictoryLine
                  style={{
                    data: {
                      stroke: themeGet(`colors.${el.color}`)({ theme }),
                      strokeWidth: 3,
                      opacity: el.opacity || 0.65,
                    },
                  }}
                  data={cleanData(dataBeforeSideDate)}
                  x="timestamp"
                  interpolation="natural"
                  labels={() => ' '}
                />
                <VictoryLine
                  style={{
                    data: {
                      stroke: themeGet(`colors.${el.color}`)({ theme }),
                      strokeWidth: 3,
                      opacity: 0.4,
                    },
                  }}
                  data={cleanData(dataAfterSideDate)}
                  x="timestamp"
                  interpolation="natural"
                  labels={() => ' '}
                />
              </VictoryGroup>
            );
          }
          
          return (
            <VictoryGroup key={`${el.id}`}>
              {/* Blocco per i dati prima di sideDate */}
              <VictoryArea
                style={{
                  data: {
                    fill: themeGet(`colors.${el.color}`)({ theme }),
                    stroke: themeGet(`colors.${el.color}`)({ theme }),
                    opacity: el.opacity || 0.65,
                  },
                }}
                data={dataBeforeSideDate}
                x="timestamp"
                interpolation="natural"
              />
              {/* Blocco per i dati dopo sideDate */}
              <VictoryArea
                style={{
                  data: {
                    fill: themeGet(`colors.${el.color}`)({ theme }),
                    stroke: themeGet(`colors.${el.color}`)({ theme }),
                    opacity: 0.4,
                  },
                }}
                data={dataAfterSideDate}
                x="timestamp"
                interpolation="natural"
              />
            </VictoryGroup>
          );
        })}

        {isSideDateActive && sideDate && chart && chart.length > 0 && chart[0].data && chart[0].data.length > 0 && (
          (() => {
            const firstDate = moment(chart[0].data[0].timestamp);
            const lastDate = moment(chart[0].data[chart[0].data.length - 1].timestamp);
            const sideDateMoment = moment(sideDate);
            
            // Mostra la sideDate solo se è nel range dei dati
            if (sideDateMoment.isBetween(firstDate, lastDate, null, '[]')) {
              // Converti la sideDate nel formato corretto per VictoryChart
              const sideDateForChart = sideDateMoment.format('YYYY-MM-DDTHH:mm:ss[Z]');
              
              // Ensure maxAxisY is valid before using it
              const validMaxAxisY = typeof maxAxisY === 'number' && Number.isFinite(maxAxisY) && maxAxisY > 0 ? maxAxisY : 100;
              
              return (
                <VictoryLine
                  data={cleanData([
                    { timestamp: sideDateForChart, x: sideDateForChart, y: 0 },
                    { timestamp: sideDateForChart, x: sideDateForChart, y: validMaxAxisY },
                  ])}
                  style={{
                    data: { stroke: 'red', strokeDasharray: '4', strokeWidth: 2 },
                  }}
                  labels={() => translations[languageCode].sideDateText}
                  labelComponent={
                    <VictoryTooltip
                      constrainToVisibleArea
                      flyoutStyle={{ stroke: 'red', strokeWidth: 1, fill: 'white' }}
                      flyoutPadding={10}
                      orientation="top"
                    />
                  }
                />
              );
            }
            return null;
          })()
        )}
      </VictoryChart>
    </Wrapper>
  );
}

export default Area;
